/**
 * Vòng lặp game (M2): requestAnimationFrame, tích lũy thời gian thực
 * thành giờ game. 1 giờ game = 60 giây thực ở tốc độ 1× (master-plan 3.3).
 *
 * Vòng lặp không chứa luật: mỗi giờ game đủ thì gọi callback để UI
 * tick core; render (khách, ánh sáng) cập nhật theo dt.
 */
import type { DuLieuGame, TrangThaiGame } from '../core/types.js';
import { gioTrongNgay } from '../core/khach.js';
import type { QuanLyKhach } from './khach.js';
import type { GameScene } from './scene.js';

export interface NguonTrangThai {
  state: TrangThaiGame;
  duLieu: DuLieuGame;
}

export interface TuyChonVongLap {
  scene: GameScene;
  quanLyKhach: QuanLyKhach;
  /** Lấy state hiện tại (UI controller cung cấp). */
  layTrangThai: () => NguonTrangThai;
  /** Gọi mỗi khi đủ 1 giờ game (UI tick core + tự lưu). */
  trenMoiGioGame: () => void;
}

export interface VongLap {
  batDau(): void;
  dung(): void;
  dangChay(): boolean;
  /** 1 = tốc độ chuẩn; 2, 8 = tua nhanh (master-plan mục 6). */
  datTocDo(tocDo: number): void;
  layTocDo(): number;
}

/** Giây thực cho 1 giờ game ở tốc độ 1×. */
export const GIAY_THUC_MOI_GIO_GAME = 60;

export function taoVongLap(tuyChon: TuyChonVongLap): VongLap {
  const { scene, quanLyKhach, layTrangThai, trenMoiGioGame } = tuyChon;
  let idKhungHinh: number | null = null;
  let lanCuoi = 0;
  let tichLuyGio = 0;
  let tocDo = 1;

  const khungHinh = (thoiDiem: number): void => {
    idKhungHinh = requestAnimationFrame(khungHinh);
    // Kẹp dt để tab ẩn lâu không gây spiral khi quay lại.
    const dtGiay = Math.min(0.25, Math.max(0, (thoiDiem - lanCuoi) / 1000 || 0));
    lanCuoi = thoiDiem;

    const gioGameKhungNay = (dtGiay * tocDo) / GIAY_THUC_MOI_GIO_GAME;
    tichLuyGio += gioGameKhungNay;
    let gioDaTick = 0;
    while (tichLuyGio >= 1) {
      tichLuyGio -= 1;
      gioDaTick += 1;
      trenMoiGioGame();
    }

    const { state, duLieu } = layTrangThai();
    quanLyKhach.capNhat(dtGiay, gioGameKhungNay, state, duLieu);
    scene.capNhatAnhSang(gioTrongNgay(state.tongGioGame));
    scene.render();
    void gioDaTick;
  };

  return {
    batDau: () => {
      if (idKhungHinh !== null) return;
      lanCuoi = performance.now();
      tichLuyGio = 0;
      idKhungHinh = requestAnimationFrame(khungHinh);
    },
    dung: () => {
      if (idKhungHinh !== null) {
        cancelAnimationFrame(idKhungHinh);
        idKhungHinh = null;
      }
    },
    dangChay: () => idKhungHinh !== null,
    datTocDo: (t: number) => {
      tocDo = t > 0 ? t : 1;
    },
    layTocDo: () => tocDo,
  };
}
